import { useState } from "react";
import { Modal, Input, Select, FormStack, Button, Alert } from "@marekl/adminer/components";
import { useGenerateBarcodes } from "../../../lib/queries/barcodeBank";
import type { ImportResult } from "../../../lib/queries/barcodeBank";
import { useVoucherTypes } from "../../../lib/queries/voucherTypes";
import { notifyError } from "@marekl/adminer/api";

interface GenerateModalProps {
  open: boolean;
  onClose: () => void;
}

export function GenerateModal({ open, onClose }: GenerateModalProps) {
  const generate = useGenerateBarcodes();
  const { data: voucherTypes = [] } = useVoucherTypes();
  const [voucherTypeId, setVoucherTypeId] = useState("");
  const [count, setCount] = useState("50");
  const [result, setResult] = useState<ImportResult | null>(null);

  const activeTypes = voucherTypes.filter((vt) => vt.active);
  const selectedType = activeTypes.find((vt) => String(vt.id) === voucherTypeId);
  const missingPrefix = !!selectedType && !selectedType.barcode_type_digits;

  const countNum = Number(count);
  const countInvalid =
    count.trim() === "" || !Number.isInteger(countNum) || countNum < 1 || countNum > 1000;
  const countError =
    count.trim() !== "" && countInvalid ? "Zadejte celé číslo v rozsahu 1–1000." : undefined;

  function handleClose() {
    setVoucherTypeId("");
    setCount("50");
    setResult(null);
    onClose();
  }

  async function handleGenerate() {
    const vtId = Number(voucherTypeId);
    if (!vtId || countInvalid) return;
    const cnt = countNum;
    try {
      const res = await generate.mutateAsync({ voucher_type_id: vtId, count: cnt });
      setResult(res);
    } catch (err) {
      notifyError(err);
    }
  }

  return (
    <Modal
      open={open}
      title="Generovat kódy"
      onClose={handleClose}
      width={480}
      footer={
        result ? (
          <Button variant="primary" onClick={handleClose}>
            Zavřít
          </Button>
        ) : (
          <>
            <Button onClick={handleClose}>Zrušit</Button>
            <Button
              variant="primary"
              loading={generate.isPending}
              onClick={handleGenerate}
              disabled={!voucherTypeId || countInvalid || missingPrefix}
            >
              Generovat
            </Button>
          </>
        )
      }
    >
      {result ? (
        <FormStack>
          <Alert variant="ok">
            Vygenerováno {result.inserted} nových kódů
            {result.skipped > 0 && `, přeskočeno ${result.skipped} (duplicity)`}.
          </Alert>
          {(result.rejected?.length ?? 0) > 0 && (
            <Alert variant="warn">
              Odmítnuto {result.rejected!.length} kódů:{" "}
              {result.rejected!.slice(0, 5).map((r) => r.barcode).join(", ")}
              {result.rejected!.length > 5 && ` … a ${result.rejected!.length - 5} dalších`}.
            </Alert>
          )}
        </FormStack>
      ) : (
        <FormStack>
          {activeTypes.length === 0 ? (
            <Alert variant="warn">
              Nejsou definovány žádné aktivní typy voucherů.
            </Alert>
          ) : (
            <>
              <Select
                  label="Typ voucheru"
                  id="gen-type"
                  value={voucherTypeId}
                  onChange={(e) => setVoucherTypeId(e.target.value)}
                >
                  <option value="">— vyberte typ —</option>
                  {activeTypes.map((vt) => (
                    <option key={vt.id} value={vt.id}>
                      {vt.name}
                    </option>
                  ))}
              </Select>
              <Input
                id="gen-count"
                label="Počet kódů (1–1000)"
                type="number"
                min={1}
                max={1000}
                value={count}
                onChange={(e) => setCount(e.target.value)}
                error={countError}
              />
              {missingPrefix && (
                <Alert variant="err">
                  Tento typ voucheru nemá nastavený EAN prefix (barcode_type_digits).
                  Nastavte jej ve Správě typů voucherů před generováním.
                </Alert>
              )}
            </>
          )}
        </FormStack>
      )}
    </Modal>
  );
}
